import { Database, Hash, Type } from 'lucide-react'
import { FramerObjectDisplay } from './FramerObjectDisplay'

interface FramerCollection {
  id?: string
  name: string
  managedBy?: string
  readonly?: boolean
  fields?: Array<{
    id?: string
    name?: string
    type?: string
    required?: boolean
    [key: string]: any
  }>
  fieldCount?: number
}

interface FramerCollectionDisplayProps {
  collections: FramerCollection[]
  title?: string
  showDetails?: boolean
  className?: string
}

/**
 * Specialized component for displaying Framer CMS collections with field schemas
 */
export function FramerCollectionDisplay({
  collections,
  title = 'Collections',
  showDetails = false,
  className = '',
}: FramerCollectionDisplayProps) {
  if (!collections || collections.length === 0) {
    return (
      <div className={`rounded-lg border border-gray-200 bg-gray-50 p-4 ${className}`}>
        <div className="flex items-center gap-2 text-gray-600">
          <Database className="h-4 w-4" />
          <span className="text-sm font-medium">No collections found</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center gap-2">
        <Database className="h-5 w-5 text-blue-600" />
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
        <span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
          {collections.length} {collections.length === 1 ? 'collection' : 'collections'}
        </span>
      </div>

      <div className="space-y-2">
        {collections.map((collection, index) => (
          <CollectionItem
            key={collection.id || collection.name || index}
            collection={collection}
            showDetails={showDetails}
          />
        ))}
      </div>
    </div>
  )
}

interface CollectionItemProps {
  collection: FramerCollection
  showDetails: boolean
}

function CollectionItem({ collection, showDetails }: CollectionItemProps) {
  const fieldCount = collection.fields?.length || collection.fieldCount || 0

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Collection Header */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
            <Database className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{collection.name}</span>
              {collection.readonly && (
                <span className="rounded bg-orange-100 px-2 py-0.5 text-xs text-orange-800">
                  readonly
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              {collection.id && (
                <span className="flex items-center gap-1">
                  <Hash className="h-3 w-3" />
                  {collection.id}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Type className="h-3 w-3" />
                {fieldCount} fields
              </span>
              {collection.managedBy && <span>• managed by {collection.managedBy}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Collection Fields */}
      {showDetails && collection.fields && collection.fields.length > 0 && (
        <div className="border-t border-gray-200 p-3">
          <div className="mb-2 text-sm font-medium text-gray-700">Fields</div>
          <div className="space-y-2">
            {collection.fields.map((field, fieldIndex) => (
              <FieldItem key={field.id || field.name || fieldIndex} field={field} />
            ))}
          </div>
        </div>
      )}

      {/* Show full collection data if no specific fields structure */}
      {showDetails && (!collection.fields || collection.fields.length === 0) && (
        <div className="border-t border-gray-200 p-3">
          <FramerObjectDisplay
            title="Collection Data"
            data={collection}
            defaultExpanded={false}
            showExpandButton={true}
            icon={<Database className="h-4 w-4 text-blue-600" />}
          />
        </div>
      )}
    </div>
  )
}

interface FieldItemProps {
  field: {
    id?: string
    name?: string
    type?: string
    required?: boolean
    [key: string]: any
  }
}

function FieldItem({ field }: FieldItemProps) {
  const getFieldTypeColor = (type?: string) => {
    switch (type?.toLowerCase()) {
      case 'string':
      case 'text':
        return 'bg-green-100 text-green-800'
      case 'number':
      case 'int':
      case 'float':
        return 'bg-blue-100 text-blue-800'
      case 'boolean':
      case 'bool':
        return 'bg-purple-100 text-purple-800'
      case 'date':
      case 'datetime':
        return 'bg-orange-100 text-orange-800'
      case 'image':
      case 'file':
        return 'bg-pink-100 text-pink-800'
      case 'reference':
      case 'collectionreference':
        return 'bg-indigo-100 text-indigo-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="flex items-center justify-between rounded border border-gray-100 bg-gray-50 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-900">{field.name || 'Unnamed field'}</span>
        {field.type && (
          <span
            className={`rounded px-2 py-0.5 text-xs font-medium ${getFieldTypeColor(field.type)}`}
          >
            {field.type}
          </span>
        )}
        {field.required && (
          <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">required</span>
        )}
      </div>

      {field.id && <span className="font-mono text-xs text-gray-500">{field.id}</span>}
    </div>
  )
}
